"use client"

import Image from "next/image"
import { cn } from "@/lib/utils"
import { PersonIcon, StarIcon, TranslateIcon } from "@/components/ui/icons"
import { safeUrl } from "@/lib/url"
import { useEditRegion } from "@/lib/edit/edit-region-registry"

/** Ahead Health Google Maps reviews page — linked from the star rating on every testimonial card. */
const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/Ahead+Health+-+Preventive+health+check-ups/@47.3734617,8.5311033,17z/data=!4m18!1m9!3m8!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!2sAhead+Health+-+Preventive+health+check-ups!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp!3m7!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp?entry=ttu"

export interface TestimonialCardProps {
  name: string
  title: string
  quote: string
  avatarSrc?: string
  rating?: number
  externalUrl?: string
  className?: string
  /**
   * When true, a small TranslateIcon is rendered next to the title.
   * Used to signal that the customer's original review was in a
   * different language and the visible text is a translation.
   */
  isTranslated?: boolean
  /** Localized tooltip for the translation badge (e.g. "Translated from English"). */
  translatedFromLabel?: string
  /** Payload testimonial doc id — registers the inline-editor regions for the
   *  `headline`, `quote`, and `authorName` fields. Absent → no regions register
   *  (public build ships no `data-cms-*` metadata either way). */
  cmsId?: string
  /** BCP47 CMS locale these docs were fetched in (region provenance). */
  editLocale?: string
}

function BodyBlock({
  title,
  quote,
  isTranslated,
  translatedFromLabel,
  cmsId,
  editLocale,
}: {
  title: string
  quote: string
  isTranslated?: boolean
  translatedFromLabel?: string
  cmsId?: string
  editLocale?: string
}) {
  const titleRef = useEditRegion<HTMLSpanElement>(
    cmsId && editLocale
      ? {
          collection: "testimonials",
          id: cmsId,
          field: "headline",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const quoteRef = useEditRegion<HTMLParagraphElement>(
    cmsId && editLocale
      ? {
          collection: "testimonials",
          id: cmsId,
          field: "quote",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  return (
    <div className="flex-1">
      {/* Title excerpt — medium weight (DS Aeonik 500) so the quote
          reads as a confident headline without going as heavy as bold. */}
      <h3 className="type-body-xl text-foreground leading-snug font-medium">
        {/* Stamp only the text, not the decorative quotes, so the inline
            editor seeds + saves the bare `headline` value. */}
        &ldquo;<span ref={titleRef}>{title}</span>&rdquo;
        {isTranslated && (
          <span
            className="group/translate relative ml-1 inline-flex h-5 w-5 translate-y-[-1px] items-center justify-center rounded-full align-middle text-neutral-400 transition-colors duration-150 hover:bg-neutral-200/70 hover:text-neutral-600"
            aria-label={translatedFromLabel}
          >
            {/* Material "translate" glyph (文A). Sized to sit politely
                next to the title — small enough to read as ancillary
                metadata, large enough that the 文A shape stays
                legible. Color is light neutral; deepens on hover. */}
            <TranslateIcon width={13} height={13} />
            {/* CSS-only tooltip — opacity flips on group-hover so no JS
                state is needed and the parent card stays server-
                renderable. The card's outer <li> ships
                `overflow-visible` for review cards so this can escape
                the tile borders cleanly. */}
            <span
              role="tooltip"
              className="type-body-sm pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 -translate-x-1/2 translate-y-1 rounded-md bg-neutral-900 px-2 py-1 whitespace-nowrap text-white opacity-0 shadow-md transition-[opacity,transform] duration-150 group-hover/translate:translate-y-0 group-hover/translate:opacity-100"
            >
              {translatedFromLabel}
            </span>
          </span>
        )}
      </h3>

      {/* Full review body. Long reviews clamp at 20 lines — the card is
       *  already a link to the source, which acts as the "see more"
       *  affordance for anything that does get clipped. Short reviews
       *  whose entire text already lives in the title carry an empty
       *  `quote` and skip the body so we don't duplicate ourselves. */}
      {quote.trim() && (
        <p
          ref={quoteRef}
          className="type-body-lg mt-3 line-clamp-[20] leading-[1.2] text-black/60"
        >
          {quote}
        </p>
      )}
    </div>
  )
}

export function TestimonialCard({
  name,
  title,
  quote,
  avatarSrc,
  rating,
  externalUrl,
  className,
  isTranslated,
  translatedFromLabel,
  cmsId,
  editLocale,
}: TestimonialCardProps) {
  const safeExternalUrl = safeUrl(externalUrl)
  const hasBodyLink = safeExternalUrl !== "#"
  const nameRef = useEditRegion<HTMLSpanElement>(
    cmsId && editLocale
      ? {
          collection: "testimonials",
          id: cmsId,
          field: "authorName",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )

  const body = (
    <BodyBlock
      title={title}
      quote={quote}
      isTranslated={isTranslated}
      translatedFromLabel={translatedFromLabel}
      cmsId={cmsId}
      editLocale={editLocale}
    />
  )

  return (
    <div
      className={cn(
        "flex h-full flex-col rounded-[12px] bg-white/70 p-6",
        className
      )}
    >
      {/* Body: title + quote (linked to per-review externalUrl if provided) */}
      {hasBodyLink ? (
        <a
          href={safeExternalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-1 flex-col transition-opacity hover:opacity-90"
        >
          {body}
        </a>
      ) : (
        body
      )}

      {/* Author + stars bottom row — stars link to Google reviews (separate from card body link) */}
      <div className="mt-5 flex items-center gap-3 border-t border-neutral-100 pt-4">
        {avatarSrc ? (
          <Image
            src={avatarSrc}
            alt={name}
            width={40}
            height={40}
            className="flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div
            aria-hidden="true"
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-400"
          >
            <PersonIcon width={22} height={22} />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span ref={nameRef} className="type-body-xl text-foreground">
            {name}
          </span>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Read Ahead Health reviews on Google"
            className="flex gap-0 transition-opacity hover:opacity-80"
          >
            {Array.from({ length: rating || 5 }, (_, i) => (
              <StarIcon key={i} width={14} height={14} />
            ))}
          </a>
        </div>
      </div>
    </div>
  )
}
